import { useEffect, useRef, useState, useCallback } from 'react';
import * as maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css';
import axios from 'axios';
import LocationPanel from './LocationPanel';
import { decodeToken } from '../UserContext';
import { useWorkItemSync } from '../hooks/useWorkItemSync';

// ── Constants ─────────────────────────────────────────────────────────────────
const CONNECTION_COLORS = { fiber: '#3b82f6', copper: '#f97316', coax: '#ef4444' };
const CONNECTION_TYPES = [
  { key: 'fiber',  label: 'Fiber',  color: CONNECTION_COLORS.fiber  },
  { key: 'copper', label: 'Copper', color: CONNECTION_COLORS.copper },
  { key: 'coax',   label: 'Coax',   color: CONNECTION_COLORS.coax   },
];
const NODE_TYPES = [
  { key: 'splice-case', label: 'Splice Case', icon: '/icons/splice-case.png' },
  { key: 'tower-site',  label: 'Tower Site',  icon: '/icons/tower-site.png'  },
  { key: 'sub-station', label: 'Sub Station', icon: '/icons/sub-station.png' },
  { key: 'pop',            label: 'POP',            icon: '/icons/pop.png'          },
  { key: 'service-center', label: 'Service Center', icon: '/icons/service-center.png' },
];
const BASEMAPS = [
  {
    id: 'street',
    label: 'Street',
    icon: '🗺️',
    style: {
      version: 8,
      sources: { osm: { type: 'raster', tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'], tileSize: 256, attribution: '© OpenStreetMap contributors' } },
      layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
    },
  },
  {
    id: 'satellite',
    label: 'Satellite',
    icon: '🛰️',
    style: {
      version: 8,
      sources: { esri: { type: 'raster', tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'], tileSize: 256, attribution: '© Esri' } },
      layers: [{ id: 'esri', type: 'raster', source: 'esri' }],
    },
  },
  {
    id: 'hybrid',
    label: 'Hybrid',
    icon: '🌍',
    style: {
      version: 8,
      sources: {
        esri: { type: 'raster', tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'], tileSize: 256, attribution: '© Esri' },
        osm_labels: { type: 'raster', tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'], tileSize: 256, opacity: 0.4 },
      },
      layers: [
        { id: 'esri', type: 'raster', source: 'esri' },
        { id: 'osm_labels', type: 'raster', source: 'osm_labels', paint: { 'raster-opacity': 0.5 } },
      ],
    },
  },
  {
    id: 'topo',
    label: 'Topo',
    icon: '⛰️',
    style: {
      version: 8,
      sources: { topo: { type: 'raster', tiles: ['https://tile.opentopomap.org/{z}/{x}/{y}.png'], tileSize: 256, attribution: '© OpenTopoMap' } },
      layers: [{ id: 'topo', type: 'raster', source: 'topo' }],
    },
  },
];
const API_BASE_URL = `${window.location.protocol}//${window.location.hostname}/api`;
const AUTH_TOKEN_KEY = 'token';

// ── Auth helpers ──────────────────────────────────────────────────────────────
const getAuthToken = () => window.localStorage.getItem(AUTH_TOKEN_KEY);
const authHeaders = () => { const t = getAuthToken(); return t ? { Authorization: `Bearer ${t}` } : {}; };
const apiGet    = p     => axios.get   (`${API_BASE_URL}${p}`,    { headers: authHeaders() });
const apiPost   = (p,b) => axios.post  (`${API_BASE_URL}${p}`, b, { headers: authHeaders() });
const apiPut    = (p,b) => axios.put   (`${API_BASE_URL}${p}`, b, { headers: authHeaders() });
const apiDelete = p     => axios.delete(`${API_BASE_URL}${p}`,    { headers: authHeaders() });

// ── Geo helpers ───────────────────────────────────────────────────────────────
function getDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000, toR = d => d * Math.PI / 180;
  const dLat = toR(lat2-lat1), dLon = toR(lon2-lon1);
  const a = Math.sin(dLat/2)**2 + Math.cos(toR(lat1))*Math.cos(toR(lat2))*Math.sin(dLon/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}
function getRouteDistance(pts) {
  let t = 0;
  for (let i = 1; i < pts.length; i++) t += getDistance(pts[i-1][1], pts[i-1][0], pts[i][1], pts[i][0]);
  return t;
}
// eslint-disable-next-line no-unused-vars
function getSegmentLabel(conn, segIdx) {
  const type = (conn.type || 'cable').toUpperCase();
  const multi = Array.isArray(conn.positions) && conn.positions.length > 2;
  return `${type}-${conn.id}${multi ? `.${segIdx+1}` : ''}`;
}

// ── useIsMobile ───────────────────────────────────────────────────────────────
function useIsMobile() {
  const [m, setM] = useState(() => typeof window !== 'undefined' ? window.innerWidth <= 700 : false);
  useEffect(() => {
    const h = () => setM(window.innerWidth <= 700);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
}

// ── LoginScreen ───────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const submit = async () => {
    if (!password || loading) return;
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API_BASE_URL}/auth/login`, { password });
      window.localStorage.setItem(AUTH_TOKEN_KEY, res.data.token);
      onLogin();
    } catch { setError('Invalid password'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', backgroundColor:'#edf2f7', padding:'20px', boxSizing:'border-box' }}>
      <div style={{ width:'100%', maxWidth:'360px', backgroundColor:'white', borderRadius:'12px', padding:'24px', boxShadow:'0 8px 32px rgba(0,0,0,0.16)', boxSizing:'border-box' }}>
        <div style={{ fontWeight:'bold', fontSize:'18px', marginBottom:'6px', color:'#1a202c' }}>Fiber Platform</div>
        <div style={{ fontSize:'13px', color:'#718096', marginBottom:'18px' }}>Sign in to access map data.</div>
        <input autoFocus type="password" value={password}
          onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submit()}
          placeholder="Password"
          style={{ width:'100%', padding:'10px 12px', border:'1px solid #cbd5e0', borderRadius:'8px', fontSize:'14px', boxSizing:'border-box', marginBottom:'10px' }}
        />
        {error && <div style={{ color:'#e53e3e', fontSize:'12px', marginBottom:'10px' }}>{error}</div>}
        <button onClick={submit} disabled={!password || loading}
          style={{ width:'100%', padding:'10px', border:'none', borderRadius:'8px', backgroundColor: password && !loading ? '#3182ce' : '#a0aec0', color:'white', cursor: password && !loading ? 'pointer' : 'not-allowed', fontWeight:'bold' }}>
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
      </div>
    </div>
  );
}

// ── Toolbar ───────────────────────────────────────────────────────────────────
function Toolbar({ activeTool, setActiveTool, onClearMeasure, isMobile }) {
  const [open, setOpen] = useState(true);
  const tools = [
    { id:'node',    icon:'📍', label:'Add Node',        color:'#3182ce' },
    { id:'connect', icon:'🔗', label:'Draw Connection', color:'#805ad5' },
    { id:'measure', icon:'📏', label:'Measure',         color:'#38a169' },
    { id:'trace',   icon:'🔍', label:'Path Trace',      color:'#0891b2' },
  ];
  const handleTool = id => {
    if (activeTool === id) { setActiveTool(null); if (id === 'measure') onClearMeasure(); }
    else { setActiveTool(id); if (id !== 'measure') onClearMeasure(); }
  };
  return (
    <div style={{ position:'absolute', top:0, left:'50%', transform:'translateX(-50%)', zIndex:1100, display:'flex', flexDirection:'column', alignItems:'center', width: isMobile ? 'calc(100vw - 20px)' : 'auto' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap: isMobile ? '6px' : '8px', backgroundColor:'white', borderRadius:'0 0 12px 12px', boxShadow:'0 4px 16px rgba(0,0,0,0.15)', padding: open ? (isMobile ? '8px' : '10px 16px') : '0px 16px', maxHeight: open ? (isMobile ? '120px' : '80px') : '0px', overflow:'hidden', transition:'max-height 0.25s ease, padding 0.25s ease', border:'1px solid #e2e8f0', borderTop:'none', flexWrap: isMobile ? 'wrap' : 'nowrap', width: isMobile ? '100%' : 'auto', boxSizing:'border-box' }}>
        {tools.map(tool => (
          <button key={tool.id} onClick={() => handleTool(tool.id)}
            style={{ display:'flex', alignItems:'center', justifyContent:'center', gap: isMobile ? '4px' : '6px', padding: isMobile ? '8px 10px' : '8px 14px', backgroundColor: activeTool === tool.id ? tool.color : '#f7f9fc', color: activeTool === tool.id ? 'white' : '#4a5568', border:`1px solid ${activeTool === tool.id ? tool.color : '#e2e8f0'}`, borderRadius:'8px', cursor:'pointer', fontWeight: activeTool === tool.id ? 'bold' : 'normal', fontSize: isMobile ? '12px' : '13px', transition:'all 0.15s ease', whiteSpace:'nowrap', flex: isMobile ? '1 1 30%' : '0 0 auto', minWidth: isMobile ? '80px' : 'auto' }}>
            <span>{tool.icon}</span><span>{tool.label}</span>
            {activeTool === tool.id && !isMobile && <span style={{ fontSize:'11px', opacity:0.8 }}>ON</span>}
          </button>
        ))}
      </div>
      <div onClick={() => setOpen(o => !o)} style={{ backgroundColor:'white', border:'1px solid #e2e8f0', borderTop:'none', borderRadius:'0 0 8px 8px', padding:'3px 20px', cursor:'pointer', boxShadow:'0 3px 8px rgba(0,0,0,0.1)', fontSize:'11px', color:'#888', userSelect:'none', letterSpacing:'0.05em' }}>
        {open ? '▲ hide' : '▼ tools'}
      </div>
    </div>
  );
}

// ── Legend ────────────────────────────────────────────────────────────────────
function Legend({ connFilters, setConnFilters, nodeFilters, setNodeFilters, isMobile }) {
  const [collapsed, setCollapsed] = useState(true);
  const toggle = (filters, setFilters, key) =>
    setFilters(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div style={{
      position: 'absolute',
      bottom: isMobile ? 70 : 30,
      left: isMobile ? 8 : 10,
      zIndex: 1000,
      backgroundColor: 'white',
      border: '1px solid #ccc',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      fontSize: isMobile ? '12px' : '13px',
      overflow: 'hidden',
      minWidth: collapsed ? 'auto' : (isMobile ? '145px' : '170px'),
      maxWidth: isMobile ? 'calc(100vw - 190px)' : 'none',
    }}>
      {/* Header / toggle */}
      <div
        onClick={() => setCollapsed(o => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          padding: collapsed ? '7px 10px' : '7px 10px 4px',
          cursor: 'pointer',
          userSelect: 'none',
          fontWeight: 'bold',
        }}
      >
        <span>🗂️ Legend</span>
        <span style={{ fontSize: '10px', color: '#888' }}>{collapsed ? '▲' : '▼'}</span>
      </div>

      {/* Expandable content */}
      {!collapsed && (
        <div style={{ padding: '0 10px 10px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '11px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Connections</div>
          {CONNECTION_TYPES.map(({ key, color, label }) => (
            <div key={key} onClick={() => toggle(connFilters, setConnFilters, key)}
              style={{ display: 'flex', alignItems: 'center', marginBottom: '5px', cursor: 'pointer', opacity: connFilters[key] ? 1 : 0.4 }}>
              <input type="checkbox" checked={connFilters[key]} onChange={() => toggle(connFilters, setConnFilters, key)} style={{ marginRight: '6px', cursor: 'pointer' }} />
              <div style={{ width: '24px', height: '4px', backgroundColor: color, borderRadius: '2px', marginRight: '6px' }} />
              {label}
            </div>
          ))}
          <div style={{ borderTop: '1px solid #eee', marginTop: '6px', paddingTop: '6px', fontWeight: 'bold', marginBottom: '6px', fontSize: '11px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Nodes</div>
          {NODE_TYPES.map(({ key, label, icon }) => (
            <div key={key} onClick={() => toggle(nodeFilters, setNodeFilters, key)}
              style={{ display: 'flex', alignItems: 'center', marginBottom: '5px', cursor: 'pointer', opacity: nodeFilters[key] ? 1 : 0.4 }}>
              <input type="checkbox" checked={nodeFilters[key] ?? true} onChange={() => toggle(nodeFilters, setNodeFilters, key)} style={{ marginRight: '6px', cursor: 'pointer' }} />
              <img src={icon} alt={label} style={{ width: '16px', height: '16px', objectFit: 'contain', marginRight: '6px' }} />
              {label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── MeasurePanel ──────────────────────────────────────────────────────────────
function MeasurePanel({ points, onClear, isMobile }) {
  // points are [lng, lat] in MapLibre — convert for distance calc
  const dist = points.length > 1 ? getRouteDistance(points) : 0;
  return (
    <div style={{ position:'absolute', bottom: isMobile ? 12 : 40, left:'50%', transform:'translateX(-50%)', zIndex:1002, backgroundColor:'white', border:'1px solid #ccc', borderRadius:'10px', boxShadow:'0 4px 16px rgba(0,0,0,0.2)', padding: isMobile ? '10px 12px' : '12px 18px', minWidth: isMobile ? 0 : '260px', width: isMobile ? 'calc(100vw - 24px)' : 'auto', maxWidth:'360px', boxSizing:'border-box', fontSize:'13px' }}>
      <div style={{ fontWeight:'bold', fontSize:'14px', marginBottom:'10px' }}>📏 Measurement</div>
      {points.length < 2
        ? <div style={{ color:'#aaa', textAlign:'center', padding:'6px 0' }}>Click the map to start measuring</div>
        : <>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'10px' }}>
              {[['Meters',`${dist.toFixed(0)} m`],['Kilometers',`${(dist/1000).toFixed(3)} km`],['Feet',`${(dist*3.28084).toFixed(0)} ft`],['Miles',`${(dist/1609.34).toFixed(3)} mi`]].map(([label,value]) => (
                <div key={label} style={{ backgroundColor:'#f7f9fc', borderRadius:'6px', padding:'6px 10px', border:'1px solid #e2e8f0' }}>
                  <div style={{ fontSize:'11px', color:'#888', marginBottom:'2px' }}>{label}</div>
                  <div style={{ fontWeight:'bold', color:'#2d3748' }}>{value}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize:'11px', color:'#aaa', marginBottom:'8px' }}>{points.length} point{points.length !== 1 ? 's' : ''} · click map to add more</div>
          </>
      }
      <button onClick={onClear} style={{ width:'100%', padding:'6px', backgroundColor:'#edf2f7', border:'1px solid #e2e8f0', borderRadius:'6px', cursor:'pointer', fontSize:'12px', color:'#4a5568' }}>Clear Points</button>
    </div>
  );
}

// ── ContextMenu ───────────────────────────────────────────────────────────────
function ContextMenu({ menu, onClose }) {
  if (!menu) return null;
  return (
    <div onClick={onClose} style={{ position:'fixed', top:0, left:0, right:0, bottom:0, zIndex:2000 }}>
      <div onClick={e => e.stopPropagation()} style={{ position:'fixed', top:menu.y, left:menu.x, backgroundColor:'white', border:'1px solid #ccc', borderRadius:'8px', boxShadow:'0 4px 12px rgba(0,0,0,0.2)', zIndex:2001, minWidth:'200px', overflow:'hidden' }}>
        <div style={{ padding:'8px 12px', backgroundColor:'#f7f7f7', borderBottom:'1px solid #eee', fontWeight:'bold', fontSize:'13px', color:'#333' }}>{menu.title}</div>
        {menu.warning && <div style={{ padding:'6px 12px', backgroundColor:'#fff8e1', borderBottom:'1px solid #eee', fontSize:'12px', color:'#b7791f' }}>⚠️ {menu.warning}</div>}
        {menu.actions.map((action, i) => (
          <div key={i} onClick={() => { action.onClick(); onClose(); }}
            style={{ padding:'10px 12px', cursor:'pointer', fontSize:'13px', color: action.danger ? '#e53e3e' : '#333', borderBottom: i < menu.actions.length-1 ? '1px solid #f0f0f0' : 'none' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor='#f5f5f5'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor='white'}
          >{action.label}</div>
        ))}
      </div>
    </div>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────────
function Sidebar({ nodes, connections, open, onClose, onPan, isMobile }) {
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('nodes');
  const q = search.toLowerCase();
  const filteredNodes = nodes.filter(n => n.name?.toLowerCase().includes(q) || n.type?.toLowerCase().includes(q));
  const filteredConns = connections.filter(c => {
    const from = nodes.find(n => n.id === c.from_id);
    const to   = nodes.find(n => n.id === c.to_id);
    return from?.name?.toLowerCase().includes(q) || to?.name?.toLowerCase().includes(q) || (c.type??'').toLowerCase().includes(q);
  });
  return (
    <div style={{ position:'absolute', top: isMobile ? 'auto' : 0, bottom: isMobile ? 0 : 'auto', right: isMobile ? 0 : (open ? 0 : '-320px'), transform: isMobile ? `translateY(${open ? 0 : 'calc(100% + 16px)'})` : 'none', width: isMobile ? '100%' : '300px', maxHeight: isMobile ? '68vh' : 'none', height: isMobile ? '68vh' : '100vh', backgroundColor:'white', boxShadow:'-4px 0 12px rgba(0,0,0,0.15)', zIndex:1000, transition: isMobile ? 'transform 0.3s ease' : 'right 0.3s ease', display:'flex', flexDirection:'column', borderRadius: isMobile ? '12px 12px 0 0' : 0, overflow:'hidden' }}>
      <div style={{ padding:'14px 16px', borderBottom:'1px solid #eee', display:'flex', justifyContent:'space-between', alignItems:'center', backgroundColor:'#3182ce', color:'white' }}>
        <span style={{ fontWeight:'bold', fontSize:'15px' }}>Fiber Platform</span>
        <span onClick={onClose} style={{ cursor:'pointer', fontSize:'18px', lineHeight:1 }}>✕</span>
      </div>
      <div style={{ padding:'10px 12px', borderBottom:'1px solid #eee' }}>
        <input type="text" placeholder="Search nodes or connections..." value={search} onChange={e => setSearch(e.target.value)} style={{ width:'100%', padding:'7px 10px', border:'1px solid #ccc', borderRadius:'6px', fontSize:'13px', boxSizing:'border-box' }} />
      </div>
      <div style={{ display:'flex', borderBottom:'1px solid #eee' }}>
        {['nodes','connections'].map(t => (
          <div key={t} onClick={() => setTab(t)} style={{ flex:1, textAlign:'center', padding:'9px', cursor:'pointer', fontSize:'13px', fontWeight: tab===t ? 'bold' : 'normal', borderBottom: tab===t ? '2px solid #3182ce' : '2px solid transparent', color: tab===t ? '#3182ce' : '#666' }}>
            {t === 'nodes' ? `📍 Nodes (${filteredNodes.length})` : `🔗 Connections (${filteredConns.length})`}
          </div>
        ))}
      </div>
      <div style={{ overflowY:'auto', flex:1, padding:'8px 0' }}>
        {tab === 'nodes' && filteredNodes.map(n => {
          const nd = NODE_TYPES.find(t => t.key === n.type);
          return (
            <div key={n.id} onClick={() => onPan(n)} style={{ padding:'10px 14px', borderBottom:'1px solid #f5f5f5', cursor:'pointer', fontSize:'13px', display:'flex', alignItems:'center', gap:'10px' }} onMouseEnter={e => e.currentTarget.style.backgroundColor='#f0f7ff'} onMouseLeave={e => e.currentTarget.style.backgroundColor='white'}>
              {nd && <img src={nd.icon} alt={nd.label} style={{ width:'28px', height:'28px', objectFit:'contain', flexShrink:0 }} />}
              <div>
                <div style={{ fontWeight:'bold' }}>{n.name}</div>
                <div style={{ color:'#888', fontSize:'12px', marginTop:'2px' }}>{nd?.label ?? n.type} · {parseFloat(n.latitude).toFixed(4)}, {parseFloat(n.longitude).toFixed(4)}</div>
              </div>
            </div>
          );
        })}
        {tab === 'connections' && filteredConns.map(c => {
          const from = nodes.find(n => n.id === c.from_id);
          const to   = nodes.find(n => n.id === c.to_id);
          return (
            <div key={c.id} style={{ padding:'10px 14px', borderBottom:'1px solid #f5f5f5', fontSize:'13px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                <div style={{ width:'12px', height:'4px', backgroundColor: CONNECTION_COLORS[c.type]||'gray', borderRadius:'2px', flexShrink:0 }} />
                <span style={{ fontWeight:'bold' }}>{c.type ?? 'unknown'}</span>
              </div>
              <div style={{ color:'#555', fontSize:'12px', marginTop:'3px' }}>{from?.name} → {to?.name}</div>
            </div>
          );
        })}
        {tab === 'nodes' && filteredNodes.length === 0 && <div style={{ padding:'20px', color:'#aaa', textAlign:'center', fontSize:'13px' }}>No nodes found</div>}
        {tab === 'connections' && filteredConns.length === 0 && <div style={{ padding:'20px', color:'#aaa', textAlign:'center', fontSize:'13px' }}>No connections found</div>}
      </div>
      <div style={{ padding:'10px 14px', borderTop:'1px solid #eee', fontSize:'12px', color:'#999', display:'flex', justifyContent:'space-between' }}>
        <span>{nodes.length} total nodes</span><span>{connections.length} total connections</span>
      </div>
    </div>
  );
}

// ── AddNodeModal ──────────────────────────────────────────────────────────────
function AddNodeModal({ latlng, onConfirm, onCancel, isMobile }) {
  const [name, setName] = useState('');
  const [type, setType] = useState(NODE_TYPES[0].key);
  if (!latlng) return null;
  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, backgroundColor:'rgba(0,0,0,0.4)', zIndex:3000, display:'flex', alignItems:'center', justifyContent:'center', padding: isMobile ? '16px' : 0, boxSizing:'border-box' }}>
      <div style={{ backgroundColor:'white', borderRadius:'12px', padding: isMobile ? '18px' : '24px', width: isMobile ? '100%' : '320px', maxWidth:'360px', boxSizing:'border-box', boxShadow:'0 8px 32px rgba(0,0,0,0.3)' }}>
        <div style={{ fontWeight:'bold', fontSize:'16px', marginBottom:'16px' }}>📍 Add Node</div>
        <div style={{ marginBottom:'12px' }}>
          <label style={{ fontSize:'13px', color:'#555', display:'block', marginBottom:'4px' }}>Name</label>
          <input autoFocus type="text" value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === 'Enter' && name && onConfirm(name, type)} placeholder="e.g. Main POP Site" style={{ width:'100%', padding:'8px 10px', border:'1px solid #ccc', borderRadius:'6px', fontSize:'13px', boxSizing:'border-box' }} />
        </div>
        <div style={{ marginBottom:'20px' }}>
          <label style={{ fontSize:'13px', color:'#555', display:'block', marginBottom:'8px' }}>Type</label>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
            {NODE_TYPES.map(t => (
              <div key={t.key} onClick={() => setType(t.key)} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'8px 10px', borderRadius:'8px', cursor:'pointer', border:`2px solid ${type===t.key ? '#3182ce' : '#e2e8f0'}`, backgroundColor: type===t.key ? '#ebf8ff' : 'white', transition:'all 0.15s ease' }}>
                <img src={t.icon} alt={t.label} style={{ width:'24px', height:'24px', objectFit:'contain' }} />
                <span style={{ fontSize:'12px', fontWeight: type===t.key ? 'bold' : 'normal', color: type===t.key ? '#3182ce' : '#4a5568' }}>{t.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button onClick={onCancel} style={{ flex:1, padding:'9px', backgroundColor:'#edf2f7', border:'1px solid #e2e8f0', borderRadius:'6px', cursor:'pointer', fontSize:'13px', color:'#4a5568' }}>Cancel</button>
          <button onClick={() => name && onConfirm(name, type)} disabled={!name} style={{ flex:1, padding:'9px', backgroundColor: name ? '#3182ce' : '#a0aec0', border:'none', borderRadius:'6px', cursor: name ? 'pointer' : 'not-allowed', fontSize:'13px', color:'white', fontWeight:'bold' }}>Add Node</button>
        </div>
      </div>
    </div>
  );
}

// ── ConnectionTypeModal ───────────────────────────────────────────────────────
function ConnectionTypeModal({ request, onConfirm, onCancel, isMobile }) {
  const [selectedType, setSelectedType] = useState('fiber');
  useEffect(() => { setSelectedType(request?.initialType || 'fiber'); }, [request]);
  if (!request) return null;
  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, backgroundColor:'rgba(0,0,0,0.4)', zIndex:3000, display:'flex', alignItems:'center', justifyContent:'center', padding: isMobile ? '16px' : 0, boxSizing:'border-box' }}>
      <div style={{ backgroundColor:'white', borderRadius:'12px', padding: isMobile ? '18px' : '22px', width: isMobile ? '100%' : '340px', maxWidth:'380px', boxSizing:'border-box', boxShadow:'0 8px 32px rgba(0,0,0,0.3)' }}>
        <div style={{ fontWeight:'bold', fontSize:'16px', marginBottom:'6px' }}>{request.title}</div>
        <div style={{ color:'#666', fontSize:'13px', lineHeight:1.4, marginBottom:'16px' }}>{request.description}</div>
        <div style={{ display:'grid', gap:'8px', marginBottom:'18px' }}>
          {CONNECTION_TYPES.map(t => (
            <button key={t.key} type="button" onClick={() => setSelectedType(t.key)} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', borderRadius:'8px', border:`2px solid ${selectedType===t.key ? '#3182ce' : '#e2e8f0'}`, backgroundColor: selectedType===t.key ? '#ebf8ff' : 'white', cursor:'pointer', textAlign:'left' }}>
              <span style={{ width:'34px', height:'5px', backgroundColor:t.color, borderRadius:'999px', flexShrink:0 }} />
              <span style={{ color: selectedType===t.key ? '#2b6cb0' : '#2d3748', fontWeight: selectedType===t.key ? 'bold' : 'normal', fontSize:'13px' }}>{t.label}</span>
            </button>
          ))}
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button onClick={onCancel} style={{ flex:1, padding:'9px', backgroundColor:'#edf2f7', border:'1px solid #e2e8f0', borderRadius:'6px', cursor:'pointer', fontSize:'13px', color:'#4a5568' }}>Cancel</button>
          <button onClick={() => onConfirm(selectedType)} style={{ flex:1, padding:'9px', backgroundColor:'#3182ce', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'13px', color:'white', fontWeight:'bold' }}>{request.confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ── EditNodeModal ─────────────────────────────────────────────────────────────
function EditNodeModal({ request, onConfirm, onCancel, isMobile }) {
  const [name, setName] = useState('');
  const [type, setType] = useState(NODE_TYPES[0].key);
  useEffect(() => { setName(request?.node?.name || ''); setType(request?.node?.type || NODE_TYPES[0].key); }, [request]);
  if (!request) return null;
  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, backgroundColor:'rgba(0,0,0,0.4)', zIndex:3000, display:'flex', alignItems:'center', justifyContent:'center', padding: isMobile ? '16px' : 0, boxSizing:'border-box' }}>
      <div style={{ backgroundColor:'white', borderRadius:'12px', padding: isMobile ? '18px' : '22px', width: isMobile ? '100%' : '360px', maxWidth:'400px', boxSizing:'border-box', boxShadow:'0 8px 32px rgba(0,0,0,0.3)' }}>
        <div style={{ fontWeight:'bold', fontSize:'16px', marginBottom:'16px' }}>Edit Node</div>
        <div style={{ marginBottom:'12px' }}>
          <label style={{ fontSize:'13px', color:'#555', display:'block', marginBottom:'4px' }}>Name</label>
          <input autoFocus type="text" value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === 'Enter' && name && onConfirm({ ...request.node, name, type })} style={{ width:'100%', padding:'8px 10px', border:'1px solid #ccc', borderRadius:'6px', fontSize:'13px', boxSizing:'border-box' }} />
        </div>
        <div style={{ marginBottom:'20px' }}>
          <label style={{ fontSize:'13px', color:'#555', display:'block', marginBottom:'8px' }}>Type</label>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
            {NODE_TYPES.map(t => (
              <button key={t.key} type="button" onClick={() => setType(t.key)} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'8px 10px', borderRadius:'8px', cursor:'pointer', border:`2px solid ${type===t.key ? '#3182ce' : '#e2e8f0'}`, backgroundColor: type===t.key ? '#ebf8ff' : 'white', textAlign:'left' }}>
                <img src={t.icon} alt={t.label} style={{ width:'24px', height:'24px', objectFit:'contain' }} />
                <span style={{ fontSize:'12px', fontWeight: type===t.key ? 'bold' : 'normal', color: type===t.key ? '#3182ce' : '#4a5568' }}>{t.label}</span>
              </button>
            ))}
          </div>
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button onClick={onCancel} style={{ flex:1, padding:'9px', backgroundColor:'#edf2f7', border:'1px solid #e2e8f0', borderRadius:'6px', cursor:'pointer', fontSize:'13px', color:'#4a5568' }}>Cancel</button>
          <button onClick={() => name && onConfirm({ ...request.node, name, type })} disabled={!name} style={{ flex:1, padding:'9px', backgroundColor: name ? '#3182ce' : '#a0aec0', border:'none', borderRadius:'6px', cursor: name ? 'pointer' : 'not-allowed', fontSize:'13px', color:'white', fontWeight:'bold' }}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ── ConfirmDeleteModal ────────────────────────────────────────────────────────
function ConfirmDeleteModal({ request, onConfirm, onCancel, isMobile }) {
  if (!request) return null;
  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, backgroundColor:'rgba(0,0,0,0.4)', zIndex:3000, display:'flex', alignItems:'center', justifyContent:'center', padding: isMobile ? '16px' : 0, boxSizing:'border-box' }}>
      <div style={{ backgroundColor:'white', borderRadius:'12px', padding: isMobile ? '18px' : '22px', width: isMobile ? '100%' : '340px', maxWidth:'380px', boxSizing:'border-box', boxShadow:'0 8px 32px rgba(0,0,0,0.3)' }}>
        <div style={{ fontWeight:'bold', fontSize:'16px', marginBottom:'8px', color:'#c53030' }}>{request.title}</div>
        <div style={{ color:'#555', fontSize:'13px', lineHeight:1.45, marginBottom:'18px' }}>{request.description}</div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button onClick={onCancel} style={{ flex:1, padding:'9px', backgroundColor:'#edf2f7', border:'1px solid #e2e8f0', borderRadius:'6px', cursor:'pointer', fontSize:'13px', color:'#4a5568' }}>Cancel</button>
          <button onClick={onConfirm} style={{ flex:1, padding:'9px', backgroundColor:'#e53e3e', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'13px', color:'white', fontWeight:'bold' }}>Delete</button>
        </div>
      </div>
    </div>
  );
}

// ── MapLibre helpers ──────────────────────────────────────────────────────────
function buildConnectionsGeoJSON(connections, nodes, connFilters) {
  const features = [];
  for (const conn of connections) {
    if (!connFilters[conn.type]) continue;
    const from = nodes.find(n => n.id === conn.from_id);
    const to   = nodes.find(n => n.id === conn.to_id);
    if (!from || !to) continue;
    const wps = Array.isArray(conn.waypoints) ? conn.waypoints : [];
    const coords = [
      [parseFloat(from.longitude), parseFloat(from.latitude)],
      ...wps,
      [parseFloat(to.longitude), parseFloat(to.latitude)],
    ];
    features.push({
      type: 'Feature',
      id: conn.id,
      geometry: { type: 'LineString', coordinates: coords },
      properties: { id: conn.id, type: conn.type, from_name: from.name, to_name: to.name, color: CONNECTION_COLORS[conn.type] || '#999' },
    });
  }
  return { type: 'FeatureCollection', features };
}
// eslint-disable-next-line no-unused-vars
function buildNodesGeoJSON(nodes, nodeFilters) {
  return {
    type: 'FeatureCollection',
    features: nodes
      .filter(n => nodeFilters[n.type] !== false)
      .map(n => ({
        type: 'Feature',
        id: n.id,
        geometry: { type: 'Point', coordinates: [parseFloat(n.longitude), parseFloat(n.latitude)] },
        properties: { id: n.id, name: n.name, type: n.type },
      })),
  };
}

function buildDraftGeoJSON(drawingConn, nodes, editingConn) {
  const features = [];
  if (drawingConn) {
    const from = drawingConn.fromNode;
    const coords = [[parseFloat(from.longitude), parseFloat(from.latitude)], ...drawingConn.waypoints];
    if (coords.length > 1) features.push({ type:'Feature', geometry:{ type:'LineString', coordinates:coords }, properties:{ draft:'drawing' } });
  }
  if (editingConn) {
    const from = nodes.find(n => n.id === editingConn.conn.from_id);
    const to   = nodes.find(n => n.id === editingConn.conn.to_id);
    if (from && to) {
      const coords = [[parseFloat(from.longitude), parseFloat(from.latitude)], ...editingConn.waypoints, [parseFloat(to.longitude), parseFloat(to.latitude)]];
      features.push({ type:'Feature', geometry:{ type:'LineString', coordinates:coords }, properties:{ draft:'editing' } });
      editingConn.waypoints.forEach((wp, i) => features.push({ type:'Feature', geometry:{ type:'Point', coordinates:wp }, properties:{ draft:'waypoint', idx:i } }));
    }
  }
  return { type:'FeatureCollection', features };
}

function buildMeasureGeoJSON(points) {
  const features = [];
  if (points.length > 1) features.push({ type:'Feature', geometry:{ type:'LineString', coordinates:points }, properties:{ measure:'line' } });
  points.forEach((p,i) => features.push({ type:'Feature', geometry:{ type:'Point', coordinates:p }, properties:{ measure:'point', idx:i } }));
  return { type:'FeatureCollection', features };
}

// ── Main MapView ──────────────────────────────────────────────────────────────
function BasemapSwitcher({ activeBasemap, setActiveBasemap, isMobile }) {
  return (
    <div style={{ position: 'absolute', bottom: isMobile ? 50 : 30, right: isMobile ? 8 : 10, zIndex: 1000, display: 'flex', flexDirection: isMobile ? 'row' : 'column', gap: '6px' }}>
      {BASEMAPS.map(b => (
        <button key={b.id} onClick={() => setActiveBasemap(b.id)}
          title={b.label}
          style={{ width: '40px', height: '40px', borderRadius: '8px', border: `2px solid ${activeBasemap === b.id ? '#3182ce' : '#e2e8f0'}`, backgroundColor: activeBasemap === b.id ? '#ebf8ff' : 'white', cursor: 'pointer', fontSize: '18px', boxShadow: '0 2px 6px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {b.icon}
        </button>
      ))}
    </div>
  );
}

// ── GeoExportPanel ────────────────────────────────────────────────────────────
function GeoExportPanel() {
  const [open, setOpen] = useState(false);
  const dl = fmt => {
    const token = localStorage.getItem('token');
    const a = document.createElement('a');
    a.href = `${API_BASE_URL}/geo-export?format=${fmt}&token=${token}`;
    a.click();
  };
  return (
    <div style={{ position:'absolute', bottom:80, left:'50%', transform:'translateX(-50%)', zIndex:1100 }}>
      {!open ? (
        <button onClick={() => setOpen(true)}
          style={{ padding:'7px 14px', backgroundColor:'#2d3748', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'bold', boxShadow:'0 2px 8px rgba(0,0,0,0.2)' }}>
          Export Map
        </button>
      ) : (
        <div style={{ backgroundColor:'white', borderRadius:'10px', boxShadow:'0 4px 20px rgba(0,0,0,0.2)', padding:'14px 16px', border:'1px solid #e2e8f0', display:'flex', flexDirection:'column', gap:6, minWidth:160 }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
            <span style={{ fontWeight:'bold', fontSize:'13px', color:'#1a202c' }}>Export As</span>
            <button onClick={() => setOpen(false)} style={{ background:'none', border:'none', cursor:'pointer', color:'#718096' }}>✕</button>
          </div>
          {[['GeoJSON','geojson'],['KML','kml'],['CSV (sites)','csv']].map(([label, fmt]) => (
            <button key={fmt} onClick={() => { dl(fmt); setOpen(false); }}
              style={{ padding:'7px 12px', backgroundColor:'#f7fafc', border:'1px solid #e2e8f0', borderRadius:6, cursor:'pointer', fontSize:13, textAlign:'left' }}>
              ⬇ {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── PathTracePanel ────────────────────────────────────────────────────────────
function PathTracePanel({ result, picking, onClear, isMobile }) {
  if (!result && !picking) return null;
  return (
    <div style={{ position:'absolute', top: isMobile ? 'auto' : 70, bottom: isMobile ? 80 : 'auto', right: 10, zIndex:1100, backgroundColor:'white', borderRadius:10, boxShadow:'0 4px 20px rgba(0,0,0,0.18)', padding:'14px 16px', minWidth:220, maxWidth:280, border:'1px solid #e2e8f0' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
        <span style={{ fontWeight:'bold', fontSize:'13px', color:'#1a202c' }}>Path Trace</span>
        <button onClick={onClear} style={{ background:'none', border:'none', cursor:'pointer', color:'#718096', fontSize:16 }}>✕</button>
      </div>
      {picking && !result && (
        <div style={{ fontSize:12, color:'#3182ce' }}>
          {picking === 1 ? 'Click a site to set the START point' : 'Click a site to set the END point'}
        </div>
      )}
      {result?.error && <div style={{ fontSize:12, color:'#e53e3e' }}>{result.error}</div>}
      {result?.path && (
        <>
          <div style={{ fontSize:12, color:'#718096', marginBottom:8 }}>
            <strong style={{ color:'#1a202c' }}>{result.from.name}</strong> → <strong style={{ color:'#1a202c' }}>{result.to.name}</strong>
          </div>
          <div style={{ display:'flex', gap:8, marginBottom:8, flexWrap:'wrap' }}>
            <span style={{ fontSize:11, backgroundColor:'#ebf8ff', color:'#2b6cb0', padding:'2px 8px', borderRadius:4 }}>{result.distanceMi} mi</span>
            <span style={{ fontSize:11, backgroundColor:'#ebf8ff', color:'#2b6cb0', padding:'2px 8px', borderRadius:4 }}>{result.distanceKm} km</span>
            <span style={{ fontSize:11, backgroundColor:'#f0fff4', color:'#276749', padding:'2px 8px', borderRadius:4 }}>{result.hops} hop{result.hops !== 1 ? 's' : ''}</span>
          </div>
          <div style={{ fontSize:11, color:'#4a5568', maxHeight:140, overflowY:'auto' }}>
            {result.path.map((node, i) => (
              <div key={node.id} style={{ display:'flex', alignItems:'center', gap:4, marginBottom:2 }}>
                <span style={{ color: i === 0 ? '#38a169' : i === result.path.length-1 ? '#e53e3e' : '#a0aec0', fontSize:10 }}>●</span>
                <span>{node.name}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── GeoImportPanel ────────────────────────────────────────────────────────────
function GeoImportPanel({ onImported }) {
  const [open,    setOpen]    = useState(false);
  const [status,  setStatus]  = useState(null); // null | 'loading' | {result} | {error}
  const fileRef = useRef(null);

  const handleFile = async e => {
    const file = e.target.files[0];
    if (!file) return;
    setStatus('loading');
    try {
      const text = await file.text();
      const geojson = JSON.parse(text);
      const res = await axios.post(`${API_BASE_URL}/geo-import`, geojson, {
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      });
      setStatus({ result: res.data });
      onImported();
    } catch (err) {
      setStatus({ error: err.response?.data?.error || err.message });
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div style={{ position:'absolute', bottom: 30, left: '50%', transform:'translateX(-50%)', zIndex:1100 }}>
      {!open ? (
        <button onClick={() => { setOpen(true); setStatus(null); }}
          style={{ padding:'8px 16px', backgroundColor:'#2d3748', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'13px', fontWeight:'bold', boxShadow:'0 2px 8px rgba(0,0,0,0.2)' }}>
          Import GeoJSON
        </button>
      ) : (
        <div style={{ backgroundColor:'white', borderRadius:'10px', boxShadow:'0 4px 20px rgba(0,0,0,0.2)', padding:'16px 20px', minWidth:'300px', border:'1px solid #e2e8f0' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
            <span style={{ fontWeight:'bold', fontSize:'14px', color:'#1a202c' }}>Import GeoJSON</span>
            <button onClick={() => setOpen(false)} style={{ background:'none', border:'none', cursor:'pointer', color:'#718096', fontSize:'16px' }}>✕</button>
          </div>
          <div style={{ fontSize:'12px', color:'#718096', marginBottom:'12px' }}>
            Points → map nodes &nbsp;|&nbsp; LineStrings → connections
          </div>
          <input ref={fileRef} type="file" accept=".geojson,.json" onChange={handleFile}
            style={{ width:'100%', fontSize:'13px', marginBottom:'10px' }}
            disabled={status === 'loading'} />
          {status === 'loading' && (
            <div style={{ fontSize:'13px', color:'#3182ce' }}>Importing...</div>
          )}
          {status?.result && (
            <div style={{ fontSize:'12px', color:'#276749', backgroundColor:'#f0fff4', borderRadius:'6px', padding:'8px 10px' }}>
              Nodes: +{status.result.nodes.inserted} added, {status.result.nodes.updated} updated<br/>
              Connections: +{status.result.connections.inserted} added, {status.result.connections.updated} updated
              {status.result.connections.skipped > 0 && <>, {status.result.connections.skipped} skipped (endpoints not found)</>}
              {status.result.featuresSkipped > 0 && <><br/>{status.result.featuresSkipped} unsupported feature(s) ignored</>}
            </div>
          )}
          {status?.error && (
            <div style={{ fontSize:'12px', color:'#c53030', backgroundColor:'#fff5f5', borderRadius:'6px', padding:'8px 10px' }}>
              {status.error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MapView() {
  const isMobile = useIsMobile();
  const mapContainer  = useRef(null);
  const map           = useRef(null);
  const markersRef    = useRef({});   // nodeId → maplibregl.Marker
  const popupRef      = useRef(null);
  const userMarkerRef = useRef(null);
  const watchIdRef    = useRef(null);

  const [isAuthenticated, setIsAuthenticated] = useState(() => Boolean(getAuthToken()));
  const userRole = decodeToken(getAuthToken())?.role || null;
  const [nodes,       setNodes]       = useState([]);
  const [connections, setConnections] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [connFilters, setConnFilters] = useState({ fiber:true, copper:true, coax:true });
  const [nodeFilters, setNodeFilters] = useState(Object.fromEntries(NODE_TYPES.map(t => [t.key, true])));
  const [activeTool,  setActiveTool]  = useState(null);
  const [drawingConn, setDrawingConn] = useState(null);
  const [editingConn, setEditingConn] = useState(null);
  const [measurePoints, setMeasurePoints] = useState([]);
  const [pendingNode,   setPendingNode]   = useState(null);
  const [connectionTypeRequest, setConnectionTypeRequest] = useState(null);
  const [nodeEditRequest,       setNodeEditRequest]       = useState(null);
  const [deleteRequest,         setDeleteRequest]         = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [activeBasemap, setActiveBasemap] = useState('street');
  const [userLocation, setUserLocation] = useState(null); // { lat, lng, accuracy }
  const [workOrders,    setWorkOrders]    = useState([]);
  const [tracePicking,  setTracePicking]  = useState(0); // 0=off 1=picking start 2=picking end
  const [traceFrom,     setTraceFrom]     = useState(null);
  const [traceResult,   setTraceResult]   = useState(null);

  // Keep refs for use inside map event callbacks
  const stateRef = useRef({});
  stateRef.current = { activeTool, drawingConn, editingConn, measurePoints, nodes, connections, connFilters, tracePicking, traceFrom };

  // Tracks whether we've already handled the initial style — skip redundant first setStyle call
  const basemapInitialized = useRef(false);

  // ── Load data ───────────────────────────────────────────────────────────────
  const reloadMapData = useCallback(() => {
    apiGet('/locations').then(r => setNodes(r.data)).catch(() => {});
    apiGet('/connections').then(r => setConnections(r.data)).catch(() => {});
  }, []);

  // Load work orders with location data for map markers
  useEffect(() => {
    if (!isAuthenticated) return;
    apiGet('/work-items').then(r => {
      const withCoords = (r.data || []).filter(w => w.latitude && w.longitude);
      setWorkOrders(withCoords);
    }).catch(() => {});
  }, [isAuthenticated]);

  // WebSocket sync — update work order status in real time
  useWorkItemSync(useCallback(msg => {
    if (msg.type === 'work_item_updated') {
      setWorkOrders(prev => prev.map(w =>
        w.id === msg.id ? { ...w, status: msg.status } : w
      ));
    }
  }, []));

  useEffect(() => {
    if (!isAuthenticated) return;
    const handle401 = err => { if (err.response?.status === 401) { window.localStorage.removeItem(AUTH_TOKEN_KEY); setIsAuthenticated(false); } };
    apiGet('/locations').then(r => setNodes(r.data)).catch(handle401);
    apiGet('/connections').then(r => setConnections(r.data)).catch(handle401);
  }, [isAuthenticated]);

  // ── Reset tool state ────────────────────────────────────────────────────────
  useEffect(() => {
    if (activeTool !== 'connect') { setDrawingConn(null); setEditingConn(null); }
    if (activeTool !== 'measure') setMeasurePoints([]);
    if (activeTool === 'trace') { setTracePicking(1); setTraceFrom(null); setTraceResult(null); }
    if (activeTool !== 'trace') { setTracePicking(0); setTraceFrom(null); setTraceResult(null); clearPathTrace(); }
  }, [activeTool]);

  const clearPathTrace = () => {
    if (map.current?.getSource('path-trace')) {
      map.current.getSource('path-trace').setData({ type:'FeatureCollection', features:[] });
    }
  };

  // Handle node click for path trace mode
  const handleTraceNodeClick = useCallback(async node => {
    const { tracePicking, traceFrom } = stateRef.current;
    if (tracePicking === 1) {
      setTraceFrom(node);
      setTracePicking(2);
    } else if (tracePicking === 2 && traceFrom) {
      setTracePicking(0);
      try {
        const res = await apiGet(`/path-trace?from=${traceFrom.id}&to=${node.id}`);
        const result = res.data;
        setTraceResult(result);
        // Highlight path on map
        if (result.edges && map.current?.getSource('path-trace')) {
          const nodeMap = Object.fromEntries(result.path.map(n => [n.id, n]));
          const features = result.edges.map(edge => ({
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: [
                [parseFloat(nodeMap[edge.from_id]?.longitude || 0), parseFloat(nodeMap[edge.from_id]?.latitude || 0)],
                [parseFloat(nodeMap[edge.to_id]?.longitude   || 0), parseFloat(nodeMap[edge.to_id]?.latitude   || 0)],
              ],
            },
            properties: {},
          }));
          map.current.getSource('path-trace').setData({ type:'FeatureCollection', features });
        }
      } catch {
        setTraceResult({ error: 'No path found between these locations.' });
      }
    }
  }, []);

  // ── Init MapLibre ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated || !mapContainer.current || map.current) return;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: {
        version: 8,
        sources: { osm: { type:'raster', tiles:['https://tile.openstreetmap.org/{z}/{x}/{y}.png'], tileSize:256, attribution:'© OpenStreetMap contributors' } },
        layers:  [{ id:'osm', type:'raster', source:'osm' }],
      },
      center: (() => { try { const p = JSON.parse(sessionStorage.getItem('mapPosition')); return p ? [p.lng, p.lat] : [-85, 41]; } catch { return [-85, 41]; } })(),
      zoom: (() => { try { const p = JSON.parse(sessionStorage.getItem('mapPosition')); return p ? p.zoom : 13; } catch { return 13; } })(),
      doubleClickZoom: false,
    });

    map.current.on('contextmenu', e => e.originalEvent.preventDefault());
    map.current.addControl(new maplibregl.NavigationControl(), 'top-right');

    map.current.on('load', () => {
      // Connection lines layer
      map.current.addSource('connections', { type:'geojson', data:{ type:'FeatureCollection', features:[] } });
      map.current.addLayer({ id:'connections-line', type:'line', source:'connections', paint:{ 'line-color':['get','color'], 'line-width':3, 'line-opacity':1 } });
      map.current.addLayer({ id:'connections-hit',  type:'line', source:'connections', paint:{ 'line-color':'transparent', 'line-width':12 } }); // wide hit target

      // Draft (drawing/editing) layer
      map.current.addSource('draft', { type:'geojson', data:{ type:'FeatureCollection', features:[] } });
      map.current.addLayer({ id:'draft-line',    type:'line',   source:'draft', filter:['==',['get','draft'],'drawing'], paint:{ 'line-color':'#805ad5', 'line-width':2, 'line-dasharray':[6,4] } });
      map.current.addLayer({ id:'edit-line',     type:'line',   source:'draft', filter:['==',['get','draft'],'editing'], paint:{ 'line-color':'#3182ce', 'line-width':3, 'line-dasharray':[6,4] } });
      map.current.addLayer({ id:'edit-waypoint', type:'circle', source:'draft', filter:['==',['get','draft'],'waypoint'], paint:{ 'circle-radius':5, 'circle-color':'#3182ce' } });

      // Measure layer
      map.current.addSource('measure', { type:'geojson', data:{ type:'FeatureCollection', features:[] } });
      map.current.addLayer({ id:'measure-line',  type:'line',   source:'measure', filter:['==',['get','measure'],'line'],  paint:{ 'line-color':'#38a169', 'line-width':3, 'line-dasharray':[8,5] } });
      map.current.addLayer({ id:'measure-point', type:'circle', source:'measure', filter:['==',['get','measure'],'point'], paint:{ 'circle-radius':6, 'circle-color':'#38a169', 'circle-stroke-width':2, 'circle-stroke-color':'white' } });

      // Live job markers layer
      map.current.addSource('work-orders', { type:'geojson', data:{ type:'FeatureCollection', features:[] } });
      map.current.addLayer({
        id: 'work-orders-glow', type: 'circle', source: 'work-orders',
        paint: {
          'circle-radius': 16,
          'circle-color': ['match', ['get','status'],
            'in_progress','#3b82f6', 'ready_to_work','#06b6d4',
            'fmp_pending','#f97316', 'design_review','#f97316',
            'closed','#10b981', '#f59e0b'],
          'circle-opacity': 0.2,
          'circle-blur': 1,
        },
      });
      map.current.addLayer({
        id: 'work-orders-circle', type: 'circle', source: 'work-orders',
        paint: {
          'circle-radius': 9,
          'circle-color': ['match', ['get','status'],
            'in_progress','#3b82f6', 'ready_to_work','#06b6d4',
            'fmp_pending','#f97316', 'design_review','#f97316',
            'closed','#10b981', '#f59e0b'],
          'circle-stroke-width': 2,
          'circle-stroke-color': 'white',
        },
      });
      map.current.addLayer({
        id: 'work-orders-label', type: 'symbol', source: 'work-orders',
        layout: {
          'text-field': ['get','label'],
          'text-size': 10,
          'text-offset': [0, 1.6],
          'text-anchor': 'top',
          'text-max-width': 12,
        },
        paint: { 'text-color': '#1a202c', 'text-halo-color': 'white', 'text-halo-width': 1 },
      });

      // Path trace highlight layer
      map.current.addSource('path-trace', { type:'geojson', data:{ type:'FeatureCollection', features:[] } });
      map.current.addLayer({ id:'path-trace-line', type:'line', source:'path-trace', paint:{ 'line-color':'#0891b2', 'line-width':5, 'line-dasharray':[8,4] } });

      // Work order marker click — navigate to work item detail
      map.current.on('click', 'work-orders-circle', e => {
        const id = e.features?.[0]?.properties?.id;
        if (id) window.location.href = `/work/${id}`;
      });
      map.current.on('mouseenter', 'work-orders-circle', () => { map.current.getCanvas().style.cursor = 'pointer'; });
      map.current.on('mouseleave', 'work-orders-circle', () => { map.current.getCanvas().style.cursor = ''; });

      // Click handler
      map.current.on('click', e => {
        const { activeTool, drawingConn, editingConn } = stateRef.current;
        if (!activeTool && !editingConn) return;

        const lngLat = [e.lngLat.lng, e.lngLat.lat];

        if (activeTool === 'measure') { setMeasurePoints(prev => [...prev, lngLat]); return; }
        if (editingConn) { setEditingConn(prev => ({ ...prev, waypoints:[...prev.waypoints, lngLat] })); return; }
        if (activeTool === 'connect' && drawingConn) { setDrawingConn(prev => ({ ...prev, waypoints:[...prev.waypoints, lngLat] })); return; }
        if (activeTool === 'node') { setPendingNode({ latlng:{ lat:e.lngLat.lat, lng:e.lngLat.lng } }); }
      });

      // Double-click to finish waypoint editing
      map.current.on('dblclick', async e => {
        const { editingConn } = stateRef.current;
        if (!editingConn) return;
        e.originalEvent.preventDefault();
        try {
          const res = await apiPut(`/connections/${editingConn.conn.id}`, { ...editingConn.conn, waypoints: editingConn.waypoints });
          setConnections(prev => prev.map(c => c.id === editingConn.conn.id ? res.data : c));
        } catch (err) { console.log('Waypoint save error:', err); }
        setEditingConn(null);
      });

      // Right-click on connection
      map.current.on('contextmenu', 'connections-hit', e => {
        e.originalEvent.preventDefault();
        const { connections, nodes } = stateRef.current;
        const props = e.features[0]?.properties;
        if (!props) return;
        const conn = connections.find(c => c.id === props.id);
        if (!conn) return;
        const from = nodes.find(n => n.id === conn.from_id);
        const to   = nodes.find(n => n.id === conn.to_id);
        setContextMenu({
          x: e.point.x, y: e.point.y,
          title: `🔗 ${from?.name} → ${to?.name}`,
          warning: `This will permanently remove the ${conn.type} connection.`,
          actions: [
            { label:'✏️ Edit Type', onClick:() => setConnectionTypeRequest({ mode:'edit', title:'Edit connection type', description:`${from?.name||'Unknown'} to ${to?.name||'Unknown'}`, confirmLabel:'Save', initialType:conn.type||'fiber', conn }) },
            { label:'✏️ Edit Waypoints', onClick:() => { setActiveTool('connect'); setEditingConn({ conn, waypoints: Array.isArray(conn.waypoints) ? conn.waypoints : [] }); } },
            { label:'🗑️ Clear Waypoints', onClick: async () => { try { const res = await apiPut(`/connections/${conn.id}`, { ...conn, waypoints:[] }); setConnections(prev => prev.map(c => c.id===conn.id ? res.data : c)); } catch(err){ console.log(err); } } },
            { label:'🗑️ Delete Connection', danger:true, onClick:() => setDeleteRequest({ kind:'connection', conn, title:'Delete connection?', description:`Delete the ${conn.type} connection from "${from?.name}" to "${to?.name}"? This cannot be undone.` }) },
          ],
        });
      });

      map.current.on('mouseenter', 'connections-hit', () => { map.current.getCanvas().style.cursor = 'pointer'; });
      map.current.on('mouseleave', 'connections-hit', () => { map.current.getCanvas().style.cursor = ''; });

      setMapReady(true);
    });

    return () => { if (map.current) { map.current.remove(); map.current = null; } };
  }, [isAuthenticated]);

  // ── Sync work order markers to map ─────────────────────────────────────────
  useEffect(() => {
    if (!mapReady || !map.current) return;
    const src = map.current.getSource('work-orders');
    if (!src) return;
    src.setData({
      type: 'FeatureCollection',
      features: workOrders
        .filter(w => w.status !== 'closed')
        .map(w => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [parseFloat(w.longitude), parseFloat(w.latitude)] },
          properties: {
            id: w.id,
            status: w.status,
            label: w.title ? (w.title.length > 22 ? w.title.slice(0, 22) + '…' : w.title) : '',
          },
        })),
    });
  }, [workOrders, mapReady]);

  // ── Sync connections to map ─────────────────────────────────────────────────
  useEffect(() => {
    if (!mapReady || !map.current) return;
    const src = map.current.getSource('connections');
    if (src) src.setData(buildConnectionsGeoJSON(connections, nodes, connFilters));
  }, [connections, nodes, connFilters, mapReady]);

  // ── Sync node markers to map ────────────────────────────────────────────────
  useEffect(() => {
    if (!mapReady || !map.current) return;

    const visible = nodes.filter(n => nodeFilters[n.type] !== false);
    const visibleIds = new Set(visible.map(n => n.id));

    // Remove markers for deleted/hidden nodes
    Object.keys(markersRef.current).forEach(id => {
      if (!visibleIds.has(Number(id))) { markersRef.current[id].remove(); delete markersRef.current[id]; }
    });

    visible.forEach(n => {
      const nd = NODE_TYPES.find(t => t.key === n.type);
      const iconUrl = nd?.icon || '/icons/pop.png';

     if (markersRef.current[n.id]) {
     markersRef.current[n.id].setLngLat([parseFloat(n.longitude), parseFloat(n.latitude)]);
     // Update label and icon in case name or type changed
     const existingEl = markersRef.current[n.id].getElement();
     const label = existingEl.querySelector('div');
     if (label) label.textContent = n.name || '';
     const img = existingEl.querySelector('img');
     if (img) img.src = NODE_TYPES.find(t => t.key === n.type)?.icon || '/icons/pop.png';
     return;
     }
      // Build custom HTML marker
      const el = document.createElement('div');
      el.style.cssText = 'display:flex;flex-direction:column;align-items:center;width:48px;cursor:pointer;';
      el.innerHTML = `
        <img src="${iconUrl}" onerror="this.src='/icons/pop.png'" style="width:48px;height:48px;object-fit:contain;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.4));pointer-events:none;" />
        <div style="margin-top:3px;background:rgba(255,255,255,0.92);border:1px solid #ccc;border-radius:4px;padding:1px 5px;font-size:11px;font-weight:600;color:#222;white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,0.2);max-width:100px;overflow:hidden;text-overflow:ellipsis;pointer-events:none;">${n.name||''}</div>
      `;

      // Click / tap
      el.addEventListener('click', () => {
        const { activeTool, drawingConn } = stateRef.current;
        if (activeTool === 'connect') {
          if (!drawingConn) { setDrawingConn({ fromNode:n, waypoints:[] }); return; }
          if (drawingConn.fromNode.id === n.id) { setDrawingConn(null); return; }
          setConnectionTypeRequest({ mode:'create', title:'Choose connection type', description:`${drawingConn.fromNode.name} to ${n.name}`, confirmLabel:'Create', initialType:'fiber', fromNode:drawingConn.fromNode, toNode:n, waypoints:drawingConn.waypoints });
          return;
        }
        // Mobile: single tap opens location panel
        // Path trace mode — node click picks from/to
        if (activeTool === 'trace') {
          const { nodes } = stateRef.current;
          const currentNode = nodes.find(node => node.id === n.id) || n;
          handleTraceNodeClick(currentNode);
          return;
        }

        // Desktop: single click pans to node (right-click opens panel)
        const isTouchDevice = window.matchMedia('(pointer: coarse)').matches;
        if (isTouchDevice) {
          const { nodes } = stateRef.current;
          const currentNode = nodes.find(node => node.id === n.id) || n;
          setSelectedLocation(currentNode);
        } else {
          map.current.flyTo({ center:[parseFloat(n.longitude), parseFloat(n.latitude)], zoom:15 });
        }
      });

      // Right-click (desktop only)
      el.addEventListener('contextmenu', e => {
        e.preventDefault();
        const { nodes } = stateRef.current;
        const currentNode = nodes.find(node => node.id === n.id) || n;
        setSelectedLocation(currentNode);
      });

      // Popup on hover
      el.addEventListener('mouseenter', () => {
        if (popupRef.current) popupRef.current.remove();
        popupRef.current = new maplibregl.Popup({ closeButton:false, offset:60 })
          .setLngLat([parseFloat(n.longitude), parseFloat(n.latitude)])
          .setHTML(`<div style="font-size:13px"><div style="font-weight:bold;margin-bottom:4px">${n.name}</div><div style="color:#666">${NODE_TYPES.find(t=>t.key===n.type)?.label??n.type}</div><div style="color:#999;font-size:11px;margin-top:4px">${parseFloat(n.latitude).toFixed(5)}, ${parseFloat(n.longitude).toFixed(5)}</div></div>`)
          .addTo(map.current);
      });
      el.addEventListener('mouseleave', () => { if (popupRef.current) { popupRef.current.remove(); popupRef.current = null; } });

      const marker = new maplibregl.Marker({ element:el, anchor:'bottom', draggable:false })
        .setLngLat([parseFloat(n.longitude), parseFloat(n.latitude)])
        .addTo(map.current);

      markersRef.current[n.id] = marker;
    });
  }, [nodes, nodeFilters, mapReady]);

  // ── Sync draft lines ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapReady || !map.current) return;
    const src = map.current.getSource('draft');
    if (src) src.setData(buildDraftGeoJSON(drawingConn, nodes, editingConn));
  }, [drawingConn, editingConn, nodes, mapReady]);

  // ── Sync measure lines ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapReady || !map.current) return;
    const src = map.current.getSource('measure');
    if (src) src.setData(buildMeasureGeoJSON(measurePoints));
  }, [measurePoints, mapReady]);

  // ── Cursor style ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapReady || !map.current) return;
    const canvas = map.current.getCanvas();
    canvas.style.cursor = { node:'crosshair', connect:'cell', measure:'crosshair' }[activeTool] || 'grab';
  }, [activeTool, mapReady]);

  useEffect(() => {
    if (!mapReady || !map.current) return;
    // Skip the very first trigger — map already initialized with street style
    // and sources were added in the map.on('load') handler
    if (!basemapInitialized.current) {
      basemapInitialized.current = true;
      return;
    }
    const basemap = BASEMAPS.find(b => b.id === activeBasemap);
    if (!basemap) return;
    map.current.setStyle(basemap.style);
    // Re-add data sources after style change, using stateRef for latest data
    map.current.once('style.load', () => {
      const { connections: c, nodes: n, connFilters: cf } = stateRef.current;
      if (!map.current.getSource('connections')) {
        map.current.addSource('connections', { type: 'geojson', data: buildConnectionsGeoJSON(c, n, cf) });
      }
      if (!map.current.getLayer('connections-line')) {
        map.current.addLayer({ id: 'connections-line', type: 'line', source: 'connections', paint: { 'line-color': ['get', 'color'], 'line-width': 3 } });
      }
      if (!map.current.getLayer('connections-hit')) {
        map.current.addLayer({ id: 'connections-hit', type: 'line', source: 'connections', paint: { 'line-color': 'transparent', 'line-width': 12 } });
      }
      if (!map.current.getSource('draft')) {
        map.current.addSource('draft', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      }
      if (!map.current.getLayer('draft-line')) {
        map.current.addLayer({ id: 'draft-line', type: 'line', source: 'draft', filter: ['==', ['get', 'draft'], 'drawing'], paint: { 'line-color': '#805ad5', 'line-width': 2, 'line-dasharray': [6, 4] } });
      }
      if (!map.current.getLayer('edit-line')) {
        map.current.addLayer({ id: 'edit-line', type: 'line', source: 'draft', filter: ['==', ['get', 'draft'], 'editing'], paint: { 'line-color': '#3182ce', 'line-width': 3, 'line-dasharray': [6, 4] } });
      }
      if (!map.current.getLayer('edit-waypoint')) {
        map.current.addLayer({ id: 'edit-waypoint', type: 'circle', source: 'draft', filter: ['==', ['get', 'draft'], 'waypoint'], paint: { 'circle-radius': 5, 'circle-color': '#3182ce' } });
      }
      if (!map.current.getSource('measure')) {
        map.current.addSource('measure', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      }
      if (!map.current.getLayer('measure-line')) {
        map.current.addLayer({ id: 'measure-line', type: 'line', source: 'measure', filter: ['==', ['get', 'measure'], 'line'], paint: { 'line-color': '#38a169', 'line-width': 3, 'line-dasharray': [8, 5] } });
      }
      if (!map.current.getLayer('measure-point')) {
        map.current.addLayer({ id: 'measure-point', type: 'circle', source: 'measure', filter: ['==', ['get', 'measure'], 'point'], paint: { 'circle-radius': 6, 'circle-color': '#38a169', 'circle-stroke-width': 2, 'circle-stroke-color': 'white' } });
      }
    });
  }, [activeBasemap, mapReady]);

  // ── User location tracker ───────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated || !navigator.geolocation) return;
    watchIdRef.current = navigator.geolocation.watchPosition(
      pos => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => {},
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
    );
    return () => { if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current); };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!mapReady || !map.current) return;
    if (!userLocation) return;

    if (!userMarkerRef.current) {
      const el = document.createElement('div');
      el.style.cssText = 'position:relative;width:20px;height:20px;';
      el.innerHTML = `
        <div style="position:absolute;inset:0;border-radius:50%;background:rgba(59,130,246,0.25);animation:pulse-ring 1.8s ease-out infinite;"></div>
        <div style="position:absolute;inset:3px;border-radius:50%;background:#3b82f6;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4);"></div>
      `;
      if (!document.getElementById('user-location-style')) {
        const style = document.createElement('style');
        style.id = 'user-location-style';
        style.textContent = `@keyframes pulse-ring{0%{transform:scale(1);opacity:0.7}100%{transform:scale(3);opacity:0}}`;
        document.head.appendChild(style);
      }
      userMarkerRef.current = new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([userLocation.lng, userLocation.lat])
        .addTo(map.current);
    } else {
      userMarkerRef.current.setLngLat([userLocation.lng, userLocation.lat]);
    }
  }, [userLocation, mapReady]);

  const locateMe = useCallback(() => {
    if (!userLocation || !map.current) return;
    map.current.flyTo({ center: [userLocation.lng, userLocation.lat], zoom: 16 });
  }, [userLocation]);

  // ── Pan to node ─────────────────────────────────────────────────────────────
  const panTo = useCallback(node => {
    if (map.current) map.current.flyTo({ center:[parseFloat(node.longitude), parseFloat(node.latitude)], zoom:15 });
  }, []);

  // ── Confirm handlers ────────────────────────────────────────────────────────
  const confirmAddNode = async (name, type) => {
    if (!pendingNode) return;
    try {
      const res = await apiPost('/locations', { name, type, latitude:pendingNode.latlng.lat, longitude:pendingNode.latlng.lng });
      setNodes(prev => [...prev, res.data]);
    } catch(err){ console.log('POST error:', err); }
    setPendingNode(null);
  };

  const confirmConnectionType = async type => {
    if (!connectionTypeRequest) return;
    try {
      if (connectionTypeRequest.mode === 'create') {
        const res = await apiPost('/connections', { from_id:connectionTypeRequest.fromNode.id, to_id:connectionTypeRequest.toNode.id, type, waypoints:connectionTypeRequest.waypoints });
        setConnections(prev => [...prev, res.data]);
        setDrawingConn(null);
      } else {
        const res = await apiPut(`/connections/${connectionTypeRequest.conn.id}`, { ...connectionTypeRequest.conn, type });
        setConnections(prev => prev.map(c => c.id === connectionTypeRequest.conn.id ? res.data : c));
      }
    } catch(err){ console.log('Connection type save error:', err); }
    setConnectionTypeRequest(null);
  };

  const cancelConnectionType = () => {
    if (connectionTypeRequest?.mode === 'create') setDrawingConn(null);
    setConnectionTypeRequest(null);
  };

  const confirmEditNode = async updatedNode => {
    try {
      const res = await apiPut(`/locations/${updatedNode.id}`, updatedNode);
      setNodes(prev => prev.map(n => n.id === updatedNode.id ? res.data : n));
    } catch(err){ console.log('Node save error:', err); }
    setNodeEditRequest(null);
  };

  const confirmDelete = async () => {
    if (!deleteRequest) return;
    try {
      if (deleteRequest.kind === 'node') {
        await apiDelete(`/locations/${deleteRequest.node.id}`);
        setNodes(prev => prev.filter(n => n.id !== deleteRequest.node.id));
        setConnections(prev => prev.filter(c => c.from_id !== deleteRequest.node.id && c.to_id !== deleteRequest.node.id));
        if (markersRef.current[deleteRequest.node.id]) { markersRef.current[deleteRequest.node.id].remove(); delete markersRef.current[deleteRequest.node.id]; }
      } else {
        await apiDelete(`/connections/${deleteRequest.conn.id}`);
        setConnections(prev => prev.filter(c => c.id !== deleteRequest.conn.id));
      }
    } catch(err){
      const msg = err?.response?.data?.error || err?.message || 'Delete failed.';
      alert(msg);
    }
    setDeleteRequest(null);
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  if (!isAuthenticated) return <LoginScreen onLogin={() => setIsAuthenticated(true)} />;

  const cursorStyle = { node:'crosshair', connect:'cell', measure:'crosshair' }[activeTool] || 'grab';

  return (
    <div style={{ position:'relative', width:'100vw', height:'100vh', overflow:'hidden', cursor:cursorStyle }}>
      {/* MapLibre container */}
      <div ref={mapContainer} style={{ height:'100vh', width:'100%' }} />

      {/* UI overlays */}
      <Toolbar activeTool={activeTool} setActiveTool={setActiveTool} onClearMeasure={() => setMeasurePoints([])} isMobile={isMobile} />

      <div style={{ position:'absolute', top: 10, right: isMobile ? 10 : (sidebarOpen ? 314 : 10), zIndex:1001, display:'flex', gap:6, transition: isMobile ? 'none' : 'right 0.3s ease' }}>
        {userLocation && (
          <button onClick={locateMe} title="Zoom to my location"
            style={{ padding:'8px 12px', backgroundColor:'#1e40af', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'bold', fontSize:'13px' }}>
            📍 Locate Me
          </button>
        )}
        <button onClick={() => setSidebarOpen(o => !o)}
          style={{ padding:'8px 14px', backgroundColor:'#2d3748', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'bold', fontSize:'13px' }}>
          {sidebarOpen ? '✕ Close' : '☰ Panel'}
        </button>
      </div>

      {(drawingConn || editingConn) && (
        <div style={{ position:'absolute', bottom:0, left:0, right:0, zIndex:1002, backgroundColor: drawingConn ? '#805ad5' : '#3182ce', color:'white', padding:'10px 16px', fontSize:'13px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span>{drawingConn ? `Drawing from "${drawingConn.fromNode.name}" — click map to add waypoints, click a node to finish` : `Editing waypoints — click map to add points, double-click to save`}</span>
          <button onClick={() => { setDrawingConn(null); setEditingConn(null); }} style={{ backgroundColor:'rgba(255,255,255,0.2)', border:'none', color:'white', padding:'4px 10px', borderRadius:'4px', cursor:'pointer', fontSize:'13px' }}>Cancel</button>
        </div>
      )}

      <ContextMenu menu={contextMenu} onClose={() => setContextMenu(null)} />
      <Legend connFilters={connFilters} setConnFilters={setConnFilters} nodeFilters={nodeFilters} setNodeFilters={setNodeFilters} isMobile={isMobile} />
      {activeTool === 'measure' && <MeasurePanel points={measurePoints} onClear={() => setMeasurePoints([])} isMobile={isMobile} />}

      <AddNodeModal latlng={pendingNode?.latlng} onConfirm={confirmAddNode} onCancel={() => setPendingNode(null)} isMobile={isMobile} />
      <ConnectionTypeModal request={connectionTypeRequest} onConfirm={confirmConnectionType} onCancel={cancelConnectionType} isMobile={isMobile} />
      <EditNodeModal request={nodeEditRequest} onConfirm={confirmEditNode} onCancel={() => setNodeEditRequest(null)} isMobile={isMobile} />
      <ConfirmDeleteModal request={deleteRequest} onConfirm={confirmDelete} onCancel={() => setDeleteRequest(null)} isMobile={isMobile} />

      <BasemapSwitcher activeBasemap={activeBasemap} setActiveBasemap={setActiveBasemap} isMobile={isMobile} />
      {userRole === 'admin' && <GeoImportPanel onImported={reloadMapData} />}
      <GeoExportPanel />
      <PathTracePanel
        result={traceResult}
        picking={tracePicking}
        onClear={() => { setActiveTool(null); setTraceResult(null); setTracePicking(0); clearPathTrace(); }}
        isMobile={isMobile}
      />
      <Sidebar nodes={nodes} connections={connections} open={sidebarOpen} onClose={() => setSidebarOpen(false)} onPan={panTo} isMobile={isMobile} />
      <LocationPanel
        location={selectedLocation}
        onClose={() => setSelectedLocation(null)}
        onOpenRack={rack => {
          if (map.current) {
            const center = map.current.getCenter();
            const zoom = map.current.getZoom();
            sessionStorage.setItem('mapPosition', JSON.stringify({ lng: center.lng, lat: center.lat, zoom }));
          }
          window.location.href = `/rack/${rack.id}`;
        }}
        onEdit={node => { setNodeEditRequest({ node }); setSelectedLocation(null); }}
        onDelete={node => {
          const affected = connections.filter(c => c.from_id === node.id || c.to_id === node.id);
          setDeleteRequest({ kind:'node', node, title:'Delete node?', description: affected.length > 0 ? `Delete "${node.name}" and its ${affected.length} connection(s)? This cannot be undone.` : `Delete "${node.name}"? This cannot be undone.` });
          setSelectedLocation(null);
        }}
      />
    </div>
  );
}
